/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDProgress from "components/MDProgress";

// Images
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VoicemailIcon from "@mui/icons-material/Voicemail";
import ErrorIcon from "@mui/icons-material/Error";
import { Tooltip, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { callHistory } from "service/axiosConfig";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import IconButton from "@mui/material/IconButton";

export default function data(expandedRowIndex, handleRowClick) {
  const [data, setData] = useState([]);
  const [isTableDataLoading, setIsTableDataLoading] = useState(true);
  const topicHeaders = new Set();
  const fetchData = async () => {
    try {
      setIsTableDataLoading(true); //  Start loading before the fetch
      const response = await callHistory();
      // console.log("this is response ",response);

      if (response && response.length > 0) {
        const formatted = response.map((item) => {
          const { date, time } = formatDate(item.date, item.time);
          return {
            ...item,
            date,
            time,
          };
        });
        setData(formatted);
      } else {
        setData([]); //  Set empty array if no records returned
      }
    } catch (error) {
      console.log(error);
      setData([]); // Optional: clear data on error
    } finally {
      setIsTableDataLoading(false); //  Stop loading after fetch completes
    }
  };

  const statusLabels = {
    0: "No Answer / Voicemail",
    1: "Incomplete",
    2: "Complete",
    3: "Connection Error",
  };

  const renderStatusIcon = (status) => {
    let icon = null;
    let color = "";
    let label = "";

    switch (status) {
      case 0:
        icon = <VoicemailIcon sx={{ fontSize: 24, color: "#90caf9" }} />;
        label = "No Answer / Voicemail";
        break;
      case 1:
        icon = <CheckCircleOutlineIcon sx={{ fontSize: 24, color: "#ff9800" }} />;
        label = "Partial Complete";
        break;
      case 2:
        icon = <CheckCircleIcon sx={{ fontSize: 24, color: "#4caf50" }} />;
        label = "Complete";
        break;
      case 3:
      default:
        icon = <ErrorIcon sx={{ fontSize: 24, color: "#f44336" }} />;
        label = "Connection Error";
    }

    return (
      <Tooltip title={label}>
        <Box
          sx={{
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  };
  useEffect(() => {
    fetchData();
  }, []);

  const renderSentimentEmoji = (sentiment) => {
    const labelMap = {
      1: "Negative",
      2: "Neutral",
      3: "Positive",
    };

    const svgMap = {
      1: "1",
      2: "3",
      3: "5",
    };

    if (![1, 2, 3].includes(sentiment)) return null;

    const label = labelMap[sentiment];
    const svgScore = svgMap[sentiment];
    const svgPath = `/svgs/${svgScore}.svg`;

    return (
      <Tooltip title={label}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box component="img" src={svgPath} alt={label} sx={{ width: 24, height: 24 }} />
        </Box>
      </Tooltip>
    );
  };

  data.forEach((row) => {
    if (row.topicSummaries) {
      Object.keys(row.topicSummaries).forEach((key) => topicHeaders.add(key));
    }
  });

  const renderScoreEmoji = (score) => {
    if (!score || isNaN(score) || score < 1 || score > 5) return null;

    const labelMap = {
      1: "Very Poor",
      2: "Poor",
      3: "Fair",
      4: "Good",
      5: "Very Good",
    };

    const svgPath = `/svgs/${score}.svg`; // Adjust folder if needed
    const label = labelMap[score];

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="img" src={svgPath} alt={`Score ${score}`} sx={{ width: 24, height: 24 }} />
        <span>{label}</span>
      </Box>
    );
  };

  const dynamicTopicColumns = Array.from(topicHeaders).map((topicName) => ({
    Header: topicName,
    accessor: `topicSummaries.${topicName}`,
    align: "left",
    width: 25,
    Cell: ({ value }) => renderScoreEmoji(value),
  }));
  return {
    columns: [
      {
        Header: () => null, // ✅ Fully removes header content
        disableSortBy: true,
        accessor: "expand",
        Cell: ({ row }) => {
          const isExpanded = expandedRowIndex === row.index;
          return (
            <IconButton size="small" onClick={() => handleRowClick(row.index)}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          );
        },
        width: 20,
      },
      {
        Header: "Date Time",
        accessor: (row) => `${row.date} ${row.time}`, // 👈 Combine fields
        id: "dateTime", // 👈 Needed for custom accessor
        align: "left",
        width: 40,
        sortType: (rowA, rowB) => {
          const a = new Date(rowA.values.dateTime);
          const b = new Date(rowB.values.dateTime);
          return a - b;
        },
      },
      {
        Header: "Call",
        accessor: "status",
        Cell: ({ value }) => renderStatusIcon(value),
        align: "left",
        width: 25,
      },
      {
        Header: "Sentiment",
        accessor: "sentiment",
        Cell: ({ value }) => renderSentimentEmoji(value),
        align: "left",
        width: 25,
      },
      ...dynamicTopicColumns, // ← ✅ Add all topic columns dynamically
    ],
    rows: data,
    isTableDataLoading: isTableDataLoading,
  };
}

export const formatDate = (date, time) => {
  const fullDateTime = new Date(`${date}T${time}`);
  const formattedDate = fullDateTime.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const formattedTime = fullDateTime.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { date: formattedDate, time: formattedTime };
};
